// The Android back button, which the web app never had to think about.
//
// Left alone, back walks out of the app from the picker and Android closes it. On a
// phone handed to whoever is on shift that reads as a crash, and the way back in is an
// icon on a home screen they have no reason to know about. So the app is never left by
// accident: back either closes what is open, or steps back to the picker, or does
// nothing at all.
//
// Also the reason this is a plugin rather than something a screen owns: the button
// exists on every screen, including the ones that do not care about it.
export default defineNuxtPlugin(() => {
  // Read here, before anything is awaited. Nuxt loses track of which app it is inside
  // once an async call has happened, and a composable called then throws.
  const router = useRouter()
  const device = useScanDevice()
  const overlayOpen = useOverlayOpen()

  const listen = async () => {
    const { Capacitor } = await import('@capacitor/core')

    if (!Capacitor.isNativePlatform()) {
      return
    }

    // Loaded only where it is used, keeping the native bridge out of the web bundle.
    const { App } = await import('@capacitor/app')

    App.addListener('backButton', () => {
      // Support, the maintenance panel and the unlock dialog all close on Escape
      // already, each holding its own idea of what closing means. Sending the key they
      // are listening for reuses that, rather than keeping a second list of open
      // overlays here that would go stale the first time one is added.
      if (overlayOpen.value) {
        document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }))

        return
      }

      // From the scanner or the door, back means "I am somewhere else now", which is the
      // picker. Not for an unpaired phone: the setup screen is the whole app until a
      // code has been entered.
      if (device.value.provisioned) {
        const picker = scanDeviceHome(device.value.purpose)

        if (router.currentRoute.value.path !== picker) {
          // router rather than navigateTo, for the same reason as above: this runs from
          // a native callback, long after the Nuxt context has gone.
          router.push(picker)

          return
        }
      }

      // On the picker, or on setup. Swallowed, because the alternative is a phone that
      // looks broken to the person holding it.
    })
  }

  // Deliberately not awaited. Nothing on screen depends on the back button being wired
  // up, and a scanning app should not spend its startup waiting for it.
  listen()
})
